import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { api, formatErrorDetail } from "../lib/api";
import { Plus, Pencil, Trash2, X, Users } from "lucide-react";

const EVENT_TYPES = ["AGM", "Expo", "Networking", "Workshop", "Conference"];

const emptyForm = {
    title: "",
    event_type: "AGM",
    description: "",
    venue: "",
    event_date: "",
    start_time: "10:00 AM",
    capacity: 50,
    cover_image: "",
};

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminEvents() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [showRegs, setShowRegs] = useState(null);
    const [regs, setRegs] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/events");
            setEvents(res.data);
        } finally { setLoading(false); }
    };

    // load is stable to component lifecycle; safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing(null);
        setForm(emptyForm);
        setOpenForm(true);
    };

    const openEdit = (ev) => {
        setEditing(ev);
        setForm({
            title: ev.title,
            event_type: ev.event_type,
            description: ev.description,
            venue: ev.venue,
            event_date: ev.event_date ? ev.event_date.slice(0, 16) : "",
            start_time: ev.start_time,
            capacity: ev.capacity,
            cover_image: ev.cover_image || "",
        });
        setOpenForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                capacity: parseInt(form.capacity, 10),
                event_date: new Date(form.event_date).toISOString(),
            };
            if (!payload.cover_image) delete payload.cover_image;
            if (editing) {
                await api.put(`/admin/events/${editing.id}`, payload);
                toast.success("Event updated");
            } else {
                await api.post("/admin/events", payload);
                toast.success("Event created");
            }
            setOpenForm(false);
            await load();
        } catch (err) {
            toast.error(formatErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this event? All registrations will also be removed.")) return;
        try {
            await api.delete(`/admin/events/${id}`);
            toast.success("Event deleted");
            await load();
        } catch (err) {
            toast.error(formatErrorDetail(err.response?.data?.detail));
        }
    };

    const viewRegs = async (ev) => {
        setShowRegs(ev);
        const res = await api.get(`/admin/events/${ev.id}/registrations`);
        setRegs(res.data);
    };

    return (
        <AdminLayout>
            <div className="mb-8 flex items-end justify-between" data-testid="admin-events-page">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Federation Calendar</div>
                    <h1 className="font-display text-3xl font-bold text-[#0F172A] tracking-tight">Events</h1>
                </div>
                <button onClick={openNew} data-testid="create-event-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors">
                    <Plus className="w-4 h-4" /> Create Event
                </button>
            </div>

            <div className="bg-white border border-[#E2E8F0]">
                {loading ? (
                    <div className="p-10 text-center text-sm text-[#64748B]">Loading…</div>
                ) : events.length === 0 ? (
                    <div className="p-16 text-center text-sm text-[#64748B]" data-testid="admin-events-empty">No events yet. Create your first one.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="admin-events-table">
                        <thead className="text-xs uppercase tracking-wide text-[#64748B] bg-[#F8F9FA]">
                            <tr>
                                <th className="text-left px-6 py-3 font-medium">Event</th>
                                <th className="text-left px-6 py-3 font-medium">Type</th>
                                <th className="text-left px-6 py-3 font-medium">Venue</th>
                                <th className="text-left px-6 py-3 font-medium">Date</th>
                                <th className="text-left px-6 py-3 font-medium">Registered</th>
                                <th className="text-right px-6 py-3 font-medium">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2E8F0]">
                            {events.map((ev) => (
                                <tr key={ev.id} data-testid={`event-row-${ev.id}`} className="hover:bg-[#F8F9FA]">
                                    <td className="px-6 py-4 font-medium">{ev.title}</td>
                                    <td className="px-6 py-4 text-[#64748B]">{ev.event_type}</td>
                                    <td className="px-6 py-4 text-[#64748B]">{ev.venue}</td>
                                    <td className="px-6 py-4 text-[#64748B] tabular">{formatDate(ev.event_date)}</td>
                                    <td className="px-6 py-4 text-[#64748B] tabular">{ev.registration_count || 0} / {ev.capacity}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-1 justify-end">
                                            <button onClick={() => viewRegs(ev)} data-testid={`view-regs-${ev.id}`} className="p-2 hover:bg-blue-50 text-blue-600" title="Registrations"><Users className="w-4 h-4" /></button>
                                            <button onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`} className="p-2 hover:bg-yellow-50 text-yellow-700" title="Edit"><Pencil className="w-4 h-4" /></button>
                                            <button onClick={() => remove(ev.id)} data-testid={`delete-event-${ev.id}`} className="p-2 hover:bg-red-50 text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Form Modal */}
            {openForm && (
                <div data-testid="event-form-modal" className="fixed inset-0 z-50 bg-[#0F172A]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenForm(false)}>
                    <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl font-bold text-[#0F172A]">{editing ? "Edit Event" : "Create Event"}</h3>
                            <button onClick={() => setOpenForm(false)} className="text-[#64748B] hover:text-[#0F172A]" data-testid="close-event-form"><X className="w-5 h-5" /></button>
                        </div>

                        <form onSubmit={submit} className="space-y-5">
                            <Field label="Title"><input className="adm-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" /></Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Type">
                                    <select className="adm-input" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="event-type">
                                        {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </Field>
                                <Field label="Capacity"><input type="number" min="1" className="adm-input" required value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="event-capacity" /></Field>
                            </div>
                            <Field label="Description"><textarea rows={3} className="adm-input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description" /></Field>
                            <Field label="Venue"><input className="adm-input" required value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="event-venue" /></Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Date & Time"><input type="datetime-local" className="adm-input" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-date" /></Field>
                                <Field label="Display Time"><input className="adm-input" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="event-time" placeholder="10:00 AM" /></Field>
                            </div>
                            <Field label="Cover Image URL (optional)"><input className="adm-input" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="event-image" /></Field>

                            <div className="flex gap-2 pt-4 border-t border-[#E2E8F0]">
                                <button type="button" onClick={() => setOpenForm(false)} className="flex-1 px-4 py-3 border border-[#E2E8F0] text-sm font-semibold hover:bg-[#F8F9FA]">Cancel</button>
                                <button type="submit" disabled={saving} data-testid="save-event-btn" className="flex-1 px-4 py-3 bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60">{saving ? "Saving…" : editing ? "Update Event" : "Create Event"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Registrations Modal */}
            {showRegs && (
                <div data-testid="regs-modal" className="fixed inset-0 z-50 bg-[#0F172A]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowRegs(null)}>
                    <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
                            <div>
                                <h3 className="font-display text-2xl font-bold text-[#0F172A]">{showRegs.title}</h3>
                                <p className="text-sm text-[#64748B]">{regs.length} registrations</p>
                            </div>
                            <button onClick={() => setShowRegs(null)} className="text-[#64748B] hover:text-[#0F172A]" data-testid="close-regs-modal"><X className="w-5 h-5" /></button>
                        </div>
                        {regs.length === 0 ? (
                            <div className="p-16 text-center text-sm text-[#64748B]">No registrations yet</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="text-xs uppercase tracking-wide text-[#64748B] bg-[#F8F9FA]">
                                    <tr><th className="text-left px-6 py-3">Name</th><th className="text-left px-6 py-3">Company</th><th className="text-left px-6 py-3">Email</th><th className="text-left px-6 py-3">Phone</th></tr>
                                </thead>
                                <tbody className="divide-y divide-[#E2E8F0]">
                                    {regs.map((r) => (
                                        <tr key={r.id} data-testid={`reg-row-${r.id}`}>
                                            <td className="px-6 py-3 font-medium">{r.name}</td>
                                            <td className="px-6 py-3 text-[#64748B]">{r.company}</td>
                                            <td className="px-6 py-3 text-[#64748B]">{r.email}</td>
                                            <td className="px-6 py-3 text-[#64748B]">{r.phone}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            <style>{`.adm-input { width: 100%; padding: 12px 14px; border: 1px solid #E2E8F0; font-size: 14px; color: #0F172A; background: #fff; } .adm-input:focus { outline: none; border-color: #0F172A; }`}</style>
        </AdminLayout>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-[0.1em] font-medium text-[#64748B] mb-2">{label}</label>
            {children}
        </div>
    );
}
